/**
 * Email Template Management — shadcn/ui redesign
 *
 * Visual changes only — all props and callbacks are preserved exactly.
 * Replaced: MUI Table/Paper/IconButton/Chip/Tooltip/Typography/Box → shadcn
 * Added:    KPI strip (Total/Active/Inactive), hover-reveal inline actions,
 *           variable badges with {{}} format, improved empty state.
 */
import React, { useMemo, useState } from 'react';
import { Pencil, Trash2, Mail, CheckCircle2, XCircle, LayoutList, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { EmailTemplate } from '../../services/email-templates-api';
import { formatRelativeTime } from '../../utils/dateUtils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';

interface Props {
  templates: EmailTemplate[];
  onEdit:        (template: EmailTemplate) => void;
  onDelete:      (templateId: number) => void;
  onRefresh:     () => void;
  onCreateFirst: () => void;
}

const EmailTemplateManagement: React.FC<Props> = ({
  templates,
  onEdit,
  onDelete,
  onCreateFirst,
}) => {
  // ── Pagination ─────────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(templates.length / pageSize));
  const paginatedTemplates = templates.slice((page - 1) * pageSize, page * pageSize);

  // ── KPI strip — computed from props ───────────────────────────────────────────
  const kpis = useMemo(() => [
    { label: 'Total',    value: templates.length,                               Icon: LayoutList,   className: '' },
    { label: 'Active',   value: templates.filter((t) => t.is_active).length,   Icon: CheckCircle2, className: 'text-emerald-700' },
    { label: 'Inactive', value: templates.filter((t) => !t.is_active).length,  Icon: XCircle,      className: 'text-muted-foreground' },
  ], [templates]);

  // ── Empty state ────────────────────────────────────────────────────────────────
  if (templates.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-3 py-14">
            <div className="rounded-full bg-muted p-3">
              <Mail className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">No email templates yet</p>
              <p className="mt-0.5 text-xs text-muted-foreground/70">
                Create your first template to get started
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={onCreateFirst}>
              Create Template
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">

      {/* ── KPI strip ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {kpis.map((k) => (
          <Badge
            key={k.label}
            variant="outline"
            className={cn('gap-1.5 px-3 py-1.5 text-xs font-medium', k.className)}
          >
            <k.Icon className="h-3 w-3" />
            {k.label}:{' '}
            <span className="font-semibold tabular-nums">{k.value}</span>
          </Badge>
        ))}
      </div>

      {/* ── Templates table card ───────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Templates
            <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
              ({templates.length})
            </span>
          </CardTitle>
          <CardAction>
            <span className="text-xs text-muted-foreground">
              Hover a row to reveal actions
            </span>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0 pb-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Name</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="hidden md:table-cell">Variables</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell text-right tabular-nums">Usage</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="w-20 pr-6" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedTemplates.map((template) => (
                <TableRow key={template.id} className="group">

                  {/* Name */}
                  <TableCell className="pl-6">
                    <span className="text-sm font-medium">{template.name}</span>
                  </TableCell>

                  {/* Subject — truncated */}
                  <TableCell>
                    <span className="block max-w-[200px] truncate text-sm text-muted-foreground lg:max-w-[300px]">
                      {template.subject}
                    </span>
                  </TableCell>

                  {/* Variable badges with {{}} wrapping */}
                  <TableCell className="hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 3).map((v) => (
                        <Badge
                          key={v}
                          variant="secondary"
                          className="h-5 px-1.5 font-mono text-xs"
                        >
                          {`{{${v}}}`}
                        </Badge>
                      ))}
                      {template.variables.length > 3 && (
                        <Badge
                          variant="outline"
                          className="h-5 px-1.5 text-xs text-muted-foreground"
                        >
                          +{template.variables.length - 3}
                        </Badge>
                      )}
                      {template.variables.length === 0 && (
                        <span className="text-xs text-muted-foreground/60">—</span>
                      )}
                    </div>
                  </TableCell>

                  {/* Active / Inactive badge */}
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        'gap-1 text-xs',
                        template.is_active
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      {template.is_active ? (
                        <><CheckCircle2 className="h-3 w-3" /> Active</>
                      ) : (
                        <><XCircle className="h-3 w-3" /> Inactive</>
                      )}
                    </Badge>
                  </TableCell>

                  {/* Usage count */}
                  <TableCell className="hidden text-right text-sm tabular-nums text-muted-foreground sm:table-cell">
                    {template.usage_count ?? 0}
                  </TableCell>

                  {/* Created date */}
                  <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
                    {formatRelativeTime(template.created_at)}
                  </TableCell>

                  {/* Hover-reveal inline actions (Edit + Delete) */}
                  <TableCell className="pr-6">
                    <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onEdit(template)}
                        aria-label={`Edit ${template.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => onDelete(template.id)}
                        aria-label={`Delete ${template.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-6 py-3">
              <p className="text-xs text-muted-foreground">
                Showing{' '}
                <span className="font-medium tabular-nums">
                  {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, templates.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium tabular-nums">{templates.length}</span>
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(1)} disabled={page === 1} aria-label="First page">
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p - 1)} disabled={page === 1} aria-label="Previous page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} aria-label="Next page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(totalPages)} disabled={page === totalPages} aria-label="Last page">
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTemplateManagement;
